import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSidebarContext } from '../../contexts/SidebarContext';
import { useAppContext } from '../../contexts/Storage';
import { getParentPath, getParentPageName } from '../../data/navigationPaths';
import userAvatarPlacehoder from '../../assets/images/profile_placeholder.png';
import logout from '../../assets/images/logout.png';
import crownIcon from '../../assets/images/crown.png';
import { Button } from '../ui';
import arrowLeftIcon from '../../assets/images/arrow_back.png';
import { useAuth } from '../../hooks/useAuth';
import { useGetUserSubscription } from '../../api/subscriptions';
import { useAIContext } from '../AI/AIContext';
import { useTooltipTour } from '../../tours/TooltipTourContext';
import RobotIcon from '../icons/RobotIcon';

type NavBarProps = {
  showMenuButton?: boolean;
};

const NavBar: React.FC<NavBarProps> = ({ showMenuButton = true }) => {
  const location = useLocation();
  const { toggleSidebar } = useSidebarContext();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const { logout: authLogout, isLoading } = useAuth();
  const { openChat } = useAIContext();
  const { startTour } = useTooltipTour();

  const { data: userSubscription } = useGetUserSubscription();

  const isDashboard = location.pathname === '/dashboard';
  const parentPath = getParentPath(location.pathname);

  const goToParent = () => {
    navigate(parentPath);
  };

  const handleLogout = () => {
    authLogout();
  };

  const formatPlanName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const displayText = isDashboard ? 'Balancio Pro' : getParentPageName(location.pathname);
  const linkTo = isDashboard ? '/dashboard' : parentPath;

  return (
    <nav className={`bg-bg_grey_light text-dark text-sm`}>
      <div className="ps-4 pe-2">
        <div className="flex justify-between py-2">
          <div className="flex items-center">
            {showMenuButton && (
              <button
                onClick={toggleSidebar}
                className="md:hidden mr-4 p-2 rounded-md hover:bg-grey_light transition-colors duration-200"
                aria-label="Toggle sidebar"
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <span className="w-full h-0.5 bg-primary_variant rounded"></span>
                  <span className="w-full h-0.5 bg-primary_variant rounded"></span>
                  <span className="w-full h-0.5 bg-primary_variant rounded"></span>
                </div>
              </button>
            )}
            {!isDashboard ? (
              <Button
                onClick={() => goToParent()}
                variant="back"
                className="hidden md:flex items-center mr-4 text-grey_dark border-0 pr-0 hover:text-dark"
              >
                <img
                  src={arrowLeftIcon}
                  alt="Back"
                  className="object-contain w-6 h-6 mr-1"
                />
              </Button>) : (<span className='mx-6'></span>)
            }
            <Link
              to={linkTo}
              className={`text-md ${isDashboard ? 'text-primary_variant' : 'text-grey_dark hover:text-dark'}`}
            >
              {displayText}
            </Link>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {userSubscription?.activeSubscription && (
              (() => {
                const subscriptionName = userSubscription.activeSubscription.subscription_name;
                const isFreeTrial = subscriptionName === 'free_trial';
                const isPaidPlan = !isFreeTrial;

                if (isFreeTrial) {
                  return (
                    <div className="hidden md:flex items-center bg-gradient-to-r from-secondary/20 to-secondary/10 border border-secondary/30 rounded-md px-3 py-1">
                      <span className="text-sm font-medium text-secondary">
                        Freemium
                      </span>
                    </div>
                  );
                }

                if (isPaidPlan) {
                  return (
                    <div className="hidden md:flex items-center bg-gradient-to-r from-yellow-100 to-yellow-50 border border-yellow-200 rounded-full px-3 py-1">
                      <img
                        src={crownIcon}
                        alt="Premium"
                        className="w-4 h-4 mr-2"
                      />
                      <span className="text-sm font-medium text-yellow-800">
                        {formatPlanName(subscriptionName)}
                      </span>
                    </div>
                  );
                }

                return null;
              })()
            )}

            <button
              type="button"
              onClick={() => startTour('full')}
              title="Start page tour"
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-grey_light transition-colors duration-200"
              style={{
                color: 'var(--grey-dark)',
                border: '1px solid var(--grey-light)',
              }}
              aria-label="Start page tour"
              data-tour="nav-tour"
            >
              <span className="text-sm font-semibold leading-none">?</span>
            </button>

            <button
              type="button"
              onClick={openChat}
              title="Open AI Assistant"
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-grey_light transition-colors duration-200"
              style={{ color: 'var(--primary)' }}
              aria-label="Open AI Assistant"
              data-tour="nav-ai"
            >
              <RobotIcon size={22} />
            </button>

            <Link
              to="/profile"
              className="hover:bg-grey_light hover:rounded hover:py-1 py-1 transition-colors duration-200 md:flex hidden items-center"
            >
              <h3 className="ms-2">Hi {user?.username || 'User'}</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={user?.imageUrl || userAvatarPlacehoder}
                alt="User Avatar"
                style={{
                  width: '30px',
                  height: '30px'
                }}
              />
            </Link>

            <button
              onClick={handleLogout}
              disabled={isLoading}
              className="hover:bg-grey_light hover:rounded hover:py-2 py-2 transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <h3 className="mx-2">Logout</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={logout}
                alt="logout"
                style={{
                  width: '16px',
                  height: '16px'
                }}
              />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
